import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowLeft } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useVoice } from '../contexts/voice';
import { useLanguage } from '../contexts/LanguageContext';
import { formatPrice } from '../lib/utils';

export default function CartPage() {
  const { items, removeFromCart, updateQuantity, getCartTotal, getCartCount } = useCart();
  const { sendContext } = useVoice();
  const { t } = useLanguage();

  useEffect(() => {
    const cartTotal = getCartTotal();
    const isEmpty = items.length === 0;

    sendContext({
      page: 'winkelwagen',
      cart_count: getCartCount(),
      cart_total: cartTotal,
      cart_items: items.map(i => ({
        id: i.product.id,
        name: i.product.name,
        price: i.product.price,
        quantity: i.quantity
      })),
      visible_products: items.map(i =>
        `${i.product.id}: ${i.product.brand} ${i.product.name} - €${i.product.price} x${i.quantity} = €${(i.product.price * i.quantity).toFixed(2)}`
      ),
      available_actions: isEmpty ? [
        'Naar de winkel gaan',
        'Producten bekijken',
      ] : [
        'Afrekenen (totaal: €' + cartTotal.toFixed(2) + ')',
        'Verder winkelen',
        'Product verwijderen uit winkelwagen',
        'Aantal aanpassen (meer/minder)',
        'Winkelwagen legen',
      ],
      navigation: [
        'Home / Winkel',
        'Afrekenen',
        'Bestellingen bekijken',
      ],
    });
  }, [items, sendContext, getCartCount, getCartTotal]);

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-16 animate-enter">
          <div className="text-center">
            <ShoppingBag className="w-24 h-24 mx-auto text-muted-foreground/30 mb-6" />
            <h1 className="text-2xl font-bold text-foreground mb-4">{t('cart.empty')}</h1>
            <p className="text-muted-foreground mb-8">
              {t('cart.empty_desc')}
            </p>
            <Link
              to="/"
              className="btn-primary inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('cart.continue_shopping')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8 animate-enter">
        <h1 className="text-2xl font-bold text-foreground mb-8">{t('cart.title')}</h1>

        <div className="space-y-4 mb-8">
          {items.map(({ product, quantity }, idx) => (
            <div
              key={product.id}
              className="flex gap-4 p-4 bg-card border border-border rounded-xl"
              style={{ animation: `enter 0.4s cubic-bezier(0.16, 1, 0.3, 1) ${idx * 0.05}s both` }}
            >
              <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-background">
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/96x96?text=No+Image';
                  }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  {product.brand}
                </p>
                <h3 className="font-medium text-foreground truncate">{product.name}</h3>
                <p className="text-lg font-bold text-foreground mt-1">
                  {formatPrice(product.price)}
                </p>
              </div>
              <div className="flex flex-col items-end justify-between">
                <button
                  onClick={() => removeFromCart(product.id)}
                  className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                  aria-label={t('cart.remove')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateQuantity(product.id, quantity - 1)}
                    className="p-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-8 text-center font-medium text-foreground">
                    {quantity}
                  </span>
                  <button
                    onClick={() => updateQuantity(product.id, quantity + 1)}
                    className="p-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Cart Summary */}
        <div className="glass-card p-6">
          <div className="flex justify-between items-center mb-6">
            <span className="text-lg text-foreground">{t('cart.total')}</span>
            <span className="text-2xl font-bold text-foreground">
              {formatPrice(getCartTotal())}
            </span>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/"
              className="btn-ghost flex-1 flex items-center justify-center gap-2 px-6 py-3 border border-border text-foreground rounded-xl transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('cart.continue_shopping')}
            </Link>
            <Link
              to="/afrekenen"
              className="btn-primary flex-1 px-6 py-3 bg-primary text-primary-foreground text-center font-medium rounded-xl hover:bg-primary/90 transition-colors"
            >
              {t('cart.checkout')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
